import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material"
import type { JSX } from "react"

import type { CreateDealerData } from "@crm-src/types/dealer"

interface DealerFormDialogProps {
  open: boolean
  mode: "create" | "edit"
  value: CreateDealerData
  submitting: boolean
  onChange: (value: CreateDealerData) => void
  onClose: () => void
  onSubmit: (event: React.FormEvent) => void
}

/**
 * The dealer form, once.
 *
 * Add and Edit were two ~90-line copies of the same seven fields, differing
 * only in the dialog title, the submit label and an `edit-` prefix on the
 * input ids. Two copies of a form is two places to add the next field to, and
 * one of them always gets missed.
 *
 * The `idPrefix` survives because the two dialogs can both be mounted at once
 * (Edit opens from a row while Add is closed but still rendered), and duplicate
 * DOM ids would break every `<label for>` in whichever mounted second.
 */
export function DealerFormDialog({
  open,
  mode,
  value,
  submitting,
  onChange,
  onClose,
  onSubmit,
}: DealerFormDialogProps): JSX.Element {
  const prefix = mode === "edit" ? "edit-" : ""
  const set = (patch: Partial<CreateDealerData>): void => onChange({ ...value, ...patch })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === "edit" ? "Edit Dealer" : "Add New Dealer"}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            id={`${prefix}name`}
            label="Name"
            size="small"
            fullWidth
            value={value.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Dealer Name"
            required
          />
          <TextField
            id={`${prefix}address`}
            label="Address"
            size="small"
            fullWidth
            value={value.address ?? ""}
            onChange={(e) => set({ address: e.target.value })}
            placeholder="Dealer Address"
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id={`${prefix}city`}
              label="City"
              size="small"
              fullWidth
              value={value.city ?? ""}
              onChange={(e) => set({ city: e.target.value })}
              placeholder="City"
            />
            <TextField
              id={`${prefix}state`}
              label="State"
              size="small"
              fullWidth
              value={value.state ?? ""}
              onChange={(e) => set({ state: e.target.value })}
              placeholder="State"
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id={`${prefix}postal_code`}
              label="Postal Code"
              size="small"
              fullWidth
              value={value.postal_code ?? ""}
              onChange={(e) => set({ postal_code: e.target.value })}
              placeholder="Postal Code"
            />
            <TextField
              id={`${prefix}country`}
              label="Country"
              size="small"
              fullWidth
              value={value.country ?? ""}
              onChange={(e) => set({ country: e.target.value })}
              placeholder="Country"
            />
          </Box>
          <TextField
            id={`${prefix}website`}
            label="Website"
            size="small"
            fullWidth
            value={value.website ?? ""}
            onChange={(e) => set({ website: e.target.value })}
            placeholder="Website URL"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button type="button" variant="outlined" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              {...(submitting ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
            >
              {mode === "edit" ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
